// ============================================
// Lab Ingestion Pipeline
// UC-0 Phase 1 — orchestration function
//
// Connects:
//   Lab Analyst output (BiomarkerResult[])
//   → biomarkerAliasService (alias resolution + unit conversion)
//   → biomarkerCatalog (range lookup + comparison)
//   → verdict
//
// Pure function. No side effects. No Firestore writes.
// Works with both client-side Lab Analyst and server-side labExtract.
// ============================================

import type { BiomarkerResult } from '../types';
import type { ExtractedBiomarker } from '../types/labUpload';
import type { AliasedBiomarker } from '../types/labUpload';
import type { CatalogBiomarker } from '../types/patientJourney';
import type {
  IngestionResult,
  IngestionVerdict,
  ResolvedBiomarker,
  UnresolvedName,
  RangeComparison,
  RangeStatus,
} from '../types/labIngestion';

import { resolveAndConvert, fuzzyMatch } from './biomarkerAliasService';
import { BIOMARKER_CATALOG } from '../data/biomarkers/biomarkerCatalog';
import { validateNormalizedForClinical } from './biomarkerOutputAssembly';

// ── Catalog Lookup Index ──
// Build once, reuse across calls. Keyed by canonical ID for O(1) lookup.

const catalogIndex: Map<string, CatalogBiomarker> = new Map();
for (const entry of BIOMARKER_CATALOG) {
  catalogIndex.set(entry.id, entry);
}

// ── Public API ──

/**
 * Run the full ingestion pipeline on biomarkers extracted by the Lab Analyst.
 *
 * Takes BiomarkerResult[] (free-text names from Claude) and:
 * 1. Converts each to ExtractedBiomarker format for the alias service
 * 2. Resolves aliases (exact → fuzzy → unresolved)
 * 3. Applies unit conversion where needed
 * 4. Looks up ranges from the biomarker catalog
 * 5. Compares value against conventional, functional, and XPRIZE ranges
 * 6. Assigns RangeStatus per tier
 * 7. Applies Lab Detection Gate for verdict
 *
 * @param extractedBiomarkers - Raw output from Lab Analyst (Claude Vision)
 * @param patientSex - Patient sex for dimorphic hormone routing
 * @returns IngestionResult with resolved biomarkers, unresolved names, and verdict
 */
export function runIngestionPipeline(
  extractedBiomarkers: BiomarkerResult[],
  patientSex?: 'male' | 'female',
): IngestionResult {
  const resolved: ResolvedBiomarker[] = [];
  const unresolved: UnresolvedName[] = [];

  let unitConversions = 0;
  let fuzzyMatches = 0;
  const fallbackRanges: string[] = [];

  // Map patientSex to the format biomarkerAliasService expects
  const aliasSex: 'Male' | 'Female' | undefined =
    patientSex === 'male' ? 'Male' : patientSex === 'female' ? 'Female' : undefined;

  for (const raw of extractedBiomarkers) {
    // Bridge BiomarkerResult → ExtractedBiomarker for the alias service
    const extracted: ExtractedBiomarker = {
      rawName: raw.biomarker,
      canonicalId: null,
      value: raw.value,
      unit: raw.unit,
      confidence: 1.0,
      rawText: raw.biomarker,
      pageNumber: 1,
      parseWarnings: [],
    };

    // Run through alias resolution + unit conversion
    const result = resolveAndConvert(extracted, aliasSex);

    if (result.canonicalId && result.value !== null) {
      // Determine match type from warnings
      const wasFuzzy = result.parseWarnings.some(w => w.startsWith('Fuzzy matched'));
      const wasConverted = result.parseWarnings.some(w => w.includes('→'));
      const matchConfidence = result.confidence;

      if (wasFuzzy) fuzzyMatches++;
      if (wasConverted) unitConversions++;

      // Look up catalog entry for range comparison
      const catalogEntry = catalogIndex.get(result.canonicalId);

      let ranges: ResolvedBiomarker['ranges'];
      let category: string;

      if (catalogEntry) {
        // Use catalog ranges (source of truth)
        ranges = {
          conventional: compareAgainstRange(result.value, catalogEntry.conventionalRange),
          functional: compareAgainstRange(result.value, catalogEntry.functionalRange),
          xprize: compareAgainstRange(result.value, catalogEntry.xPrizeRange),
        };
        category = catalogEntry.categories[0] || 'unknown';
      } else {
        // Fallback: use ranges from the Lab Analyst extraction
        fallbackRanges.push(result.canonicalId);
        ranges = buildFallbackRanges(result.value, raw);
        category = raw.category || 'unknown';
      }

      resolved.push({
        canonicalId: result.canonicalId,
        displayName: catalogEntry?.name || raw.biomarker,
        rawName: raw.biomarker,
        matchType: wasFuzzy ? 'fuzzy' : 'exact',
        matchConfidence,
        value: result.value,
        unit: result.unit,
        originalUnit: wasConverted ? raw.unit : undefined,
        unitConverted: wasConverted,
        ranges,
        category,
        warnings: result.parseWarnings,
      });
    } else {
      // Unresolved — could not match to any canonical ID, or value was null.
      // Run fuzzyMatch with a lower threshold to surface candidates for the admin QA UI.
      // If resolveAndConvert already found a match >= 0.85, canonicalId would be set
      // and we wouldn't be here — so any candidate found at 0.5 is a true "near miss."
      const fuzzyCandidate = fuzzyMatch(raw.biomarker, 0.5);

      unresolved.push({
        rawName: raw.biomarker,
        value: raw.value,
        unit: raw.unit,
        bestFuzzyMatch: fuzzyCandidate
          ? { canonicalId: fuzzyCandidate.id, confidence: fuzzyCandidate.confidence }
          : undefined,
      });
    }
  }

  // Lab Detection Gate + verdict
  const { verdict, verdictReason } = determineVerdict(resolved, unresolved);

  // Average confidence across resolved biomarkers
  const averageConfidence = resolved.length > 0
    ? resolved.reduce((sum, r) => sum + r.matchConfidence, 0) / resolved.length
    : 0;

  return {
    resolvedBiomarkers: resolved,
    unresolvedNames: unresolved,
    verdict,
    verdictReason,
    inputCount: extractedBiomarkers.length,
    resolvedCount: resolved.length,
    unresolvedCount: unresolved.length,
    averageConfidence: Math.round(averageConfidence * 100) / 100,
    ingestedAt: new Date().toISOString(),
    metadata: {
      patientSex,
      unitConversions,
      fuzzyMatches,
      fallbackRanges,
    },
  };
}

// ── AliasedBiomarker Ingestion (CAP.02 D#10) ──

/**
 * Run ingestion pipeline from AliasedBiomarker[] (CAP.02 output).
 * Validates isNormalized flag before any clinical calculations.
 * Non-normalized biomarkers are logged and excluded from range comparison.
 */
export function runIngestionFromAliased(
  aliasedBiomarkers: AliasedBiomarker[],
  patientSex?: 'male' | 'female',
): IngestionResult {
  // [c3] Validate isNormalized before clinical calculations
  const { safe, unsafe } = validateNormalizedForClinical(aliasedBiomarkers);

  // Log unsafe biomarkers — they are NOT used in clinical calculations
  for (const u of unsafe) {
    console.error(
      `[labIngestionPipeline] Skipping clinical calculation for "${u.originalName}" — isNormalized=false, unit="${u.unit}"`,
    );
  }

  // Convert safe AliasedBiomarkers to BiomarkerResult format for existing pipeline
  const bridged: BiomarkerResult[] = safe
    .filter((b): b is AliasedBiomarker & { value: number } => typeof b.value === 'number')
    .map((b) => ({
      biomarker: b.originalName,
      value: b.value,
      unit: b.unit,
      standardRange: b.referenceRange
        ? parseRangeString(b.referenceRange)
        : { low: 0, high: 0 },
      functionalRange: { low: 0, high: 0 },
      eliteAverage: 0,
      standardStatus: (b.flag === null || b.flag === undefined ? 'normal' : 'abnormal') as 'normal' | 'abnormal',
      hemologixStatus: 'optimal' as const,
      category: 'metabolic' as const,
      interpretation: '',
    }));

  return runIngestionPipeline(bridged, patientSex);
}

function parseRangeString(range: string): { low: number; high: number } {
  const match = range.match(/^([\d.]+)\s*[-–]\s*([\d.]+)$/);
  if (!match) return { low: 0, high: 0 };
  return { low: parseFloat(match[1]), high: parseFloat(match[2]) };
}

// ── Range Comparison ──

/**
 * Compare a value against a range and assign a RangeStatus.
 *
 * Status logic (from UC-0 spec Section 7):
 *   value < low                   → 'below'
 *   value >= low, in bottom 20%   → 'low_normal'
 *   value in middle 60%           → 'optimal'
 *   value in top 20%              → 'high_normal'
 *   value > high                  → 'above'
 */
function compareAgainstRange(
  value: number,
  range: { low: number; high: number },
): RangeComparison {
  const status = assignRangeStatus(value, range);
  return { low: range.low, high: range.high, status };
}

function assignRangeStatus(
  value: number,
  range: { low: number; high: number },
): RangeStatus {
  if (value < range.low) return 'below';
  if (value > range.high) return 'above';

  // Within range — determine position
  const span = range.high - range.low;

  // Guard against zero-width ranges (e.g. both low and high are 0)
  if (span <= 0) return 'optimal';

  const position = (value - range.low) / span;

  if (position < 0.2) return 'low_normal';
  if (position > 0.8) return 'high_normal';
  return 'optimal';
}

// ── Fallback Range Builder ──

/**
 * When a biomarker is resolved but has no catalog entry, build ranges
 * from what Claude reported in the extraction. This is a best-effort
 * fallback — the resolved biomarker will be flagged with a warning.
 */
function buildFallbackRanges(
  value: number,
  raw: BiomarkerResult,
): ResolvedBiomarker['ranges'] {
  const conventionalRange = raw.standardRange || { low: 0, high: 0 };
  const functionalRange = raw.functionalRange || conventionalRange;

  // No XPRIZE data from Claude — use functional as approximation
  const xprizeRange = functionalRange;

  return {
    conventional: compareAgainstRange(value, conventionalRange),
    functional: compareAgainstRange(value, functionalRange),
    xprize: compareAgainstRange(value, xprizeRange),
  };
}

// ── Verdict Logic ──

/**
 * Lab Detection Gate + verdict assignment.
 *
 * From UC-0 spec:
 *   < 3 resolved → 'failed' (not a lab report)
 *   3-4 resolved → 'needs_review' (partial extraction)
 *   5+ resolved  → 'clean' baseline
 *
 * Additional downgrades:
 *   Any unresolved names → downgrade to 'needs_review' at most
 *   Any fuzzy match below 0.90 → downgrade to 'needs_review' at most
 */
function determineVerdict(
  resolved: ResolvedBiomarker[],
  unresolvedNames: UnresolvedName[],
): { verdict: IngestionVerdict; verdictReason: string } {
  const resolvedCount = resolved.length;

  // Lab Detection Gate
  if (resolvedCount < 3) {
    return {
      verdict: 'failed',
      verdictReason: `Only ${resolvedCount} recognized biomarker${resolvedCount === 1 ? '' : 's'} found. Minimum 3 required to confirm this is a lab report.`,
    };
  }

  // Start with baseline based on count
  let verdict: IngestionVerdict = resolvedCount < 5 ? 'needs_review' : 'clean';
  const reasons: string[] = [];

  if (resolvedCount >= 3 && resolvedCount < 5) {
    reasons.push(`Only ${resolvedCount} biomarkers resolved (partial extraction).`);
  }

  // Check for unresolved names
  if (unresolvedNames.length > 0) {
    if (verdict === 'clean') verdict = 'needs_review';
    reasons.push(`${unresolvedNames.length} unresolved biomarker name${unresolvedNames.length === 1 ? '' : 's'}.`);
  }

  // Check for low-confidence fuzzy matches
  const lowConfidenceMatches = resolved.filter(r => r.matchType === 'fuzzy' && r.matchConfidence < 0.90);
  if (lowConfidenceMatches.length > 0) {
    if (verdict === 'clean') verdict = 'needs_review';
    reasons.push(`${lowConfidenceMatches.length} fuzzy match${lowConfidenceMatches.length === 1 ? '' : 'es'} below 0.90 confidence.`);
  }

  if (reasons.length === 0) {
    reasons.push(`All ${resolvedCount} biomarkers resolved cleanly.`);
  }

  return { verdict, verdictReason: reasons.join(' ') };
}
